import React, {ReactNode, useEffect} from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import {PipelineType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getPipelineType} from 'pipelines/PipelineTypes';
import {CenteredSpinner} from 'ui-library/Spinner';

import isPipelineGraphQLInternallyConsistent from './isPipelineGraphQLInternallyConsistent';
import {
  GET_PIPELINE_QUERY_DATA_BY_PIPELINE_TYPE,
  PipelineQueryData,
  PollablePipelineType,
} from './PollingConstants';
import {usePollingPipeline} from './usePollingPipeline';

export interface PollingPipelineProps<T extends PollablePipelineType> {
  pipelineId: string;
  children: (data: PipelineQueryData[T]) => ReactNode;
}

/**
 * Poll an analysis or analysis template via GraphQL for updates.
 */
export function PollingPipeline<T extends PollablePipelineType>(props: PollingPipelineProps<T>): JSX.Element {
  const {pipelineId} = props;
  const pipelineType = getPipelineType(pipelineId) as T;
  const result = usePollingPipeline<T>(pipelineId);

  const pipeline =
    result.type === QueryResultType.SUCCESS
      ? GET_PIPELINE_QUERY_DATA_BY_PIPELINE_TYPE[pipelineType](result.data)
      : null;

  const isInconsistent =
    pipeline != null && !isInaccessible(pipeline) && !isPipelineGraphQLInternallyConsistent(pipeline);

  /**
   * Occasionally, if the pipeline is updated as the GraphQL query response is being generated on the backend,
   * the GraphQL response may be internally inconsistent, until the next re-fetch.
   *
   * See `isPipelineGraphQLInternallyConsistent`.
   */
  useEffect(() => {
    if (!isInconsistent) {
      return undefined;
    }
    const timer = setTimeout(() => {
      result.refetch();
    }, 50);
    return () => {
      clearTimeout(timer);
    };
  }, [isInconsistent, result]);

  if (result.type !== QueryResultType.SUCCESS || pipeline == null) {
    return <CenteredSpinner />;
  }

  if (isInaccessible(pipeline)) {
    return <WorkspaceItemInaccessibleGuard inaccessibleInfo={pipeline} modelType={pipelineType} />;
  }

  if (isInconsistent) {
    const previousData = result.previousData;
    if (previousData != null) {
      const previousPipeline = GET_PIPELINE_QUERY_DATA_BY_PIPELINE_TYPE[pipelineType](previousData);
      if (!isInaccessible(previousPipeline) && isPipelineGraphQLInternallyConsistent(previousPipeline)) {
        return <>{props.children(previousPipeline)}</>;
      }
    }
    return <CenteredSpinner />;
  }

  return <>{props.children(pipeline)}</>;
}

export function PollingAnalysis({
  analysisId,
  children,
}: {
  analysisId: string;
  children: (data: PipelineQueryData[PipelineType.ANALYSIS]) => ReactNode;
}): JSX.Element {
  return <PollingPipeline pipelineId={analysisId}>{children}</PollingPipeline>;
}

export function PollingAnalysisTemplate({
  analysisTemplateId,
  children,
}: {
  analysisTemplateId: string;
  children: (data: PipelineQueryData[PipelineType.ANALYSIS_TEMPLATE]) => ReactNode;
}): JSX.Element {
  return <PollingPipeline pipelineId={analysisTemplateId}>{children}</PollingPipeline>;
}
